import { Cartao, ListaDeDados, type Dado } from '../../../components/ListaDeDados'
import { dataHora } from '../../../lib/datas'
import {
  NATUREZA_DA_SOLUCAO,
  ROTULO_CAUSA_EMERGENCIAL,
  ROTULO_NATUREZA_DA_SOLUCAO,
  ROTULO_TIPO,
} from '../../../lib/glossario'
import type { DetalheGmud } from '../../../types/gmud'

/**
 * Como a mudança foi classificada na abertura.
 *
 * Campo opcional só aparece quando existe. Mostrar "Causa da emergência: —" numa mudança de
 * prioridade baixa sugere um campo esquecido onde não há campo nenhum; o mesmo vale para módulo,
 * sub-módulo e ordem de serviço, que a maior parte das solicitações não usa.
 *
 * "Nasceu de" e não "Origem": desde que o histórico importado passou a existir, `origem` é a
 * procedência do registro (deste sistema ou do anterior), e usar a mesma palavra para duas
 * coisas na mesma tela é como o sistema anterior acabou com dois campos "status".
 *
 * O aprovador do sistema anterior NÃO entra aqui de propósito: é texto livre importado, não
 * decisão de comitê, e ao lado dos dados do processo novo passaria por uma. Ele aparece só no
 * aviso de histórico, junto da explicação do que é.
 *
 * @param props a solicitação
 * @returns o cartão de classificação
 */
export function PainelDeClassificacao({ gmud }: { gmud: DetalheGmud }) {
  const dados: Dado[] = [
    { rotulo: 'Nasceu de', valor: ROTULO_TIPO[gmud.tipo] ?? gmud.tipo },
    { rotulo: 'Chamado no e-Niterói', valor: gmud.chamadoNumero },
    // Ausente no histórico e no que foi aberto antes da V54: ninguém declarou, e a linha some em vez
    // de afirmar uma natureza que a contratada não informou.
    ...(gmud.naturezaDaSolucao
      ? [
          {
            rotulo: NATUREZA_DA_SOLUCAO.rotulo,
            valor: ROTULO_NATUREZA_DA_SOLUCAO[gmud.naturezaDaSolucao] ?? gmud.naturezaDaSolucao,
          },
        ]
      : []),
    ...(gmud.ordemServico ? [{ rotulo: 'Ordem de serviço', valor: gmud.ordemServico }] : []),
    { rotulo: 'Sistema afetado', valor: gmud.sistema },
    ...(gmud.modulo ? [{ rotulo: 'Módulo', valor: gmud.modulo }] : []),
    ...(gmud.subModulo ? [{ rotulo: 'Sub-módulo', valor: gmud.subModulo }] : []),
    { rotulo: 'Tipo de manutenção', valor: gmud.finalidade },
    { rotulo: 'Quem abriu', valor: gmud.solicitante },
    { rotulo: 'Responsável pela execução', valor: gmud.responsavel },
    { rotulo: 'Aberta em', valor: dataHora(gmud.criadoEm) },
    { rotulo: 'Última alteração', valor: dataHora(gmud.atualizadoEm) },
    ...(gmud.causaEmergencial
      ? [
          {
            rotulo: 'Causa da emergência',
            valor: ROTULO_CAUSA_EMERGENCIAL[gmud.causaEmergencial] ?? gmud.causaEmergencial,
          },
        ]
      : []),
  ]

  return (
    <Cartao titulo="Classificação" descricao="O que esta mudança é, em que sistema mexe e de quem ela é.">
      <div className="space-y-5">
        <ListaDeDados dados={dados} />
        {gmud.justificativaEmergencial && (
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Por que é emergencial</h3>
            <p className="mt-1 max-w-prose whitespace-pre-line text-sm text-slate-700">
              {gmud.justificativaEmergencial}
            </p>
          </div>
        )}
      </div>
    </Cartao>
  )
}
